import { useVendorSearch } from '@/hooks/queries'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { Vendor } from '@/lib/types'

const NONE: Vendor[] = []

export interface VendorPickerSearch {
  /** Results for exactly the typed filter; empty while a search is pending or failed. */
  results: Vendor[]
  /** The typed filter is ahead of the debounce, or its search is still loading. */
  searching: boolean
  /** The search for the typed filter failed. */
  failed: boolean
  /** The search for the typed filter succeeded (results may be empty). */
  ready: boolean
  retry: () => void
}

/**
 * KAN-822 P2 — a reader vendor picker's search, debounced. Results are offered
 * only when they answer the filter the reader has typed: while the debounce is
 * pending, or the next query is loading, nothing from an earlier query can be
 * clicked (Cubic on #1549). Shared by Tracked vendors and Vendor updates.
 */
export function useVendorPickerSearch(filter: string): VendorPickerSearch {
  const debounced = useDebouncedValue(filter)
  const search = useVendorSearch(debounced)
  const current = debounced.trim() === filter.trim()
  const ready = current && search.isSuccess === true && Array.isArray(search.data)
  return {
    results: ready ? (search.data as Vendor[]) : NONE,
    searching: !current || search.isPending === true,
    failed: current && search.isError === true,
    ready,
    retry: () => void search.refetch(),
  }
}
